/**
 * RelatedOnTopic — cross-links between courses and blog articles that
 * share a topic hub. Mounted on CourseDetail + LessonViewer footers so
 * learners jump from long-form article → structured course (or the
 * reverse) without ever hitting a dead end.
 *
 * Reads: GET /api/related?category=&themes=&title=&exclude_course_id=&exclude_lesson_id=
 * The backend infers the best-matching /topics/:slug from the hints
 * and returns up to 3 courses + 3 posts (newest first).
 *
 * Silently renders nothing when the API returns no matches, so it
 * never breaks pages for courses without a mapped topic.
 */
import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent } from './ui/card';
import { BookOpen, FileText, Compass, ArrowRight, Clock } from 'lucide-react';

const RelatedOnTopic = ({
  category = '',
  themes = [],           // string[]
  title = '',
  excludeCourseId = '',
  excludeLessonId = '',
  className = '',
  testId = 'related-on-topic',
}) => {
  const { API } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = {
          category: category || '',
          themes: (themes || []).join(','),
          title: title || '',
          exclude_course_id: excludeCourseId || '',
          exclude_lesson_id: excludeLessonId || '',
          limit: 3,
        };
        const { data: res } = await axios.get(`${API}/related`, { params });
        if (!cancelled) setData(res || null);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [API, category, themes.join(','), title, excludeCourseId, excludeLessonId]);

  if (loading || !data || !data.topic) return null;
  const { topic, courses = [], posts = [] } = data;
  if (courses.length === 0 && posts.length === 0) return null;

  return (
    <section
      className={`mt-8 rounded-2xl border border-border bg-card p-5 ${className}`}
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-primary inline-flex items-center gap-1.5">
            <Compass className="w-3 h-3" /> Related on this topic
          </div>
          <h3 className="font-heading text-xl font-extrabold mt-1">{topic.title}</h3>
          {topic.description && (
            <p className="text-xs text-muted-foreground mt-1 max-w-xl">
              {topic.description}
            </p>
          )}
        </div>
        <Link
          to={`/topics/${topic.slug}`}
          className="inline-flex items-center gap-1 text-xs font-bold text-primary hover:underline shrink-0"
          data-testid={`${testId}-view-hub`}
        >
          View topic hub <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      {courses.length > 0 && (
        <div className="mb-4">
          <div className="text-[11px] uppercase tracking-widest font-bold text-foreground/60 mb-2 inline-flex items-center gap-1.5">
            <BookOpen className="w-3 h-3" /> Courses
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {courses.map((c) => (
              <Link
                key={c.id}
                to={`/course/${c.id}`}
                className="group block"
                data-testid={`${testId}-course-${c.id}`}
              >
                <Card className="h-full transition hover:shadow-md hover:-translate-y-0.5">
                  <CardContent className="p-3">
                    <div className="font-heading font-extrabold text-sm leading-tight line-clamp-2 group-hover:text-primary">
                      {c.title}
                    </div>
                    {c.description && (
                      <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2">
                        {c.description}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}

      {posts.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-widest font-bold text-foreground/60 mb-2 inline-flex items-center gap-1.5">
            <FileText className="w-3 h-3" /> Articles
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {posts.map((p) => (
              <Link
                key={p.id}
                to={`/blog/${p.slug}`}
                className="group block"
                data-testid={`${testId}-post-${p.slug}`}
              >
                <Card className="h-full transition hover:shadow-md hover:-translate-y-0.5">
                  <CardContent className="p-3">
                    <div className="font-heading font-extrabold text-sm leading-tight line-clamp-2 group-hover:text-primary">
                      {p.title}
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground">
                      <Clock className="w-3 h-3" /> {p.reading_minutes || 3} min
                    </div>
                    {p.excerpt && (
                      <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2">
                        {p.excerpt}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default RelatedOnTopic;
